// Podcast Ad's script as live's form reads it (spec G14-templates, PR 16; live's
// `podcast-ad-form/core` and `library-view`): the beats each length has, the lines' limits, the
// blank script "Write the lines yourself" fills, the meta line, and what keeps the script from
// approval. The server checks the same rules on Approve (`podcast_ad_draft.script_issue`).
import type {
  PodcastLength,
  PodcastScript,
  PodcastSpeaker,
  ScriptLine,
} from "../../../api/generated/model";

/** Live's words (`core/constants.ts`, `library-view/constants.ts`). */
export const PICK_PRODUCT = "Pick a product and its packshot";
export const GENERATE_SCRIPT = "Generate or write the script";
export const LENGTH_MISMATCH = "The script was written for another length — generate or write it again";
export const ACTOR_PENDING = "Wait for the AI Actor to finish";
export const STILLS_PENDING = "Wait for all five images";
export const EMPTY_LINE = "Every line needs its text";
export const MAX_LINE_WORDS = 25;
export const LONG_LINE = `Keep every line at ${MAX_LINE_WORDS} words or fewer`;
export const UNQUOTED_CLAIMS =
  "Some product facts are not quoted from the product page — check the lines before approving.";

/** Live's lengths, in its order (`AD_LENGTHS`), and the lines each has (`LINE_COUNTS`). */
export const LENGTHS: readonly PodcastLength[] = ["45", "60", "80"];
export const LINE_COUNTS: Readonly<Record<PodcastLength, number>> = { "45": 11, "60": 15, "80": 17 };

/** The words a whole script aims at, per length (live's `WORD_TARGETS`). */
const WORD_TARGETS: Readonly<Record<PodcastLength, { min: number; max: number }>> = {
  "45": { min: 100, max: 120 },
  "60": { min: 145, max: 165 },
  "80": { min: 195, max: 220 },
};

interface Beat {
  beat: string;
  speaker: PodcastSpeaker;
}

/** The 60 s ad's beats, one a line, with who says it (live's `BEATS_60`). */
const BEATS_60: readonly Beat[] = [
  { beat: "hook", speaker: "GUEST" },
  { beat: "listener_question", speaker: "HOST" },
  { beat: "causes", speaker: "GUEST" },
  { beat: "look_for_question", speaker: "HOST" },
  { beat: "look_for", speaker: "GUEST" },
  { beat: "common_habit", speaker: "HOST" },
  { beat: "correction", speaker: "GUEST" },
  { beat: "wait_really", speaker: "HOST" },
  { beat: "product_reveal", speaker: "GUEST" },
  { beat: "mechanism_detail", speaker: "GUEST" },
  { beat: "honest_doubt", speaker: "HOST" },
  { beat: "honest_answer", speaker: "GUEST" },
  { beat: "where_to_start", speaker: "HOST" },
  { beat: "advice_close", speaker: "GUEST" },
  { beat: "sign_off", speaker: "HOST" },
];

/** Live's `BEATS_BY_LENGTH`: the 45 s ad keeps eleven of the 60 s beats (by their numbers); the
 * 80 s ad adds a question and its proof after the twelfth. */
const BEATS_45_NUMBERS = [1, 2, 3, 6, 7, 8, 9, 11, 12, 14, 15];
const EXTRA_BEATS_80_AFTER = 12;
export const BEATS: Readonly<Record<PodcastLength, readonly Beat[]>> = {
  "45": BEATS_45_NUMBERS.flatMap((number) => BEATS_60[number - 1] ?? []),
  "60": BEATS_60,
  "80": [
    ...BEATS_60.slice(0, EXTRA_BEATS_80_AFTER),
    { beat: "extra_question", speaker: "HOST" },
    { beat: "extra_proof", speaker: "GUEST" },
    ...BEATS_60.slice(EXTRA_BEATS_80_AFTER),
  ],
};

/** Live's end card button when the form gives none (`toBlankScript`). */
const DEFAULT_CTA = "Shop now";

/** Words as live counts them (`countWords`): runs of non-space. */
export function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function scriptWords(lines: readonly ScriptLine[]): number {
  return lines.reduce((total, line) => total + countWords(line.text), 0);
}

/** Live's pace (`estimateDurationSeconds`): three words a second, a quarter second a line, two
 * seconds of end card, to a tenth. */
function estimatedSeconds(lines: readonly ScriptLine[]): number {
  return Math.round((scriptWords(lines) / 3 + 0.25 * lines.length + 2) * 10) / 10;
}

/** Live's meta line over the script (`toScriptMeta`): "15 lines · 0 words (145–165) · ≈ 5.8 s". */
export function scriptMeta(lines: readonly ScriptLine[], length: PodcastLength): string {
  const { min, max } = WORD_TARGETS[length];
  return `${lines.length} lines · ${scriptWords(lines)} words (${min}–${max}) · ≈ ${estimatedSeconds(lines)} s`;
}

/** "Write the lines yourself" (live's `toBlankScript`): a blank line a beat of the length, the
 * end card the product's name and the button the CTA's (live's "Shop now" without one). */
export function blankScript(options: {
  topic: string;
  length: PodcastLength;
  productName: string;
  cta: string;
}): PodcastScript {
  const { topic, length, productName, cta } = options;
  return {
    topic,
    mechanism: "",
    length_s: Number(length),
    claims: [],
    lines: BEATS[length].map(({ beat, speaker }, index) => ({
      id: index + 1,
      beat,
      speaker,
      text: "",
      claim_ids: [],
    })),
    pronunciations: [],
    word_count: 0,
    estimated_duration_s: 0,
    end_card: { headline: productName, cta_button: cta || DEFAULT_CTA },
  };
}

/** What keeps the lines from approval, an empty line before a long one (live's `toLinesIssue`). */
function linesIssue(lines: readonly ScriptLine[]): string | null {
  if (lines.some((line) => line.text.trim() === "")) return EMPTY_LINE;
  return lines.some((line) => countWords(line.text) > MAX_LINE_WORDS) ? LONG_LINE : null;
}

/** Whether two scripts have the same lines, word for word (live's `hasSameLineTexts`). */
export function sameLineTexts(script: PodcastScript, other: PodcastScript | null): boolean {
  return (
    other !== null &&
    script.lines.length === other.lines.length &&
    script.lines.every((line, at) => line.text === other.lines[at]?.text)
  );
}

/** What keeps the script from approval (live's `toScriptIssue`): none, a script written for
 * another length, or its lines. */
export function scriptIssue(script: PodcastScript | null, length: PodcastLength): string | null {
  if (script === null) return GENERATE_SCRIPT;
  if (script.lines.length !== LINE_COUNTS[length]) return LENGTH_MISMATCH;
  return linesIssue(script.lines);
}
